import { useCallback, useMemo } from 'react';
import { useStore } from 'zustand';
import { useTabScope } from '../../contexts/TabScopeContext';
import { tabScopedStore } from './tabScopedStore';

/** Drop undefined so `{ ...defaults, ...scoped }` cannot replace defaults with `undefined`. */
function omitUndefined<V extends Record<string, unknown>>(obj: V): Partial<V> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined),
  ) as Partial<V>;
}

export function createTabScopedStore<T extends Record<string, unknown>>(
  defaults: T,
) {
  return function useTabScopedStore() {
    const scopeKey = useTabScope();

    const scopedEntry = useStore(tabScopedStore, (state) => {
      if (!scopeKey) return undefined;
      return state.entries[scopeKey] as T | undefined;
    });

    const updateEntry = useStore(tabScopedStore, (state) => state.updateEntry);
    const clearEntry = useStore(tabScopedStore, (state) => state.clearEntry);

    const filters = useMemo(
      () => ({ ...defaults, ...omitUndefined((scopedEntry ?? {}) as T) }),
      [scopedEntry],
    );

    const update = useCallback(
      (partial: Partial<T>) => {
        if (!scopeKey) return;
        updateEntry(scopeKey, partial);
      },
      [scopeKey, updateEntry],
    );

    const reset = useCallback(() => {
      if (!scopeKey) return;
      updateEntry(scopeKey, defaults);
    }, [scopeKey, updateEntry]);

    const clearTabScope = useCallback(() => {
      if (!scopeKey) return;
      clearEntry(scopeKey);
    }, [scopeKey, clearEntry]);

    return { filters, update, reset, clearTabScope };
  };
}
